import { Box, Typography, Stack, Button, Chip, CircularProgress, Alert, LinearProgress } from '@mui/joy';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import GCoinIcon from './GCoinIcon';
import Header from '../Header';
import { coinText, gcoinDate } from '../../gcoinApi';

export const surfaceSx = {
    '--joy-palette-text-secondary': '#c0c0d6',
    color: 'var(--joy-palette-text-primary)',
    '& .MuiTypography-root': { color: 'var(--joy-palette-text-primary)' },
    '& .MuiTypography-body-sm, & .MuiTypography-body-xs': { color: 'var(--joy-palette-text-secondary)' },
    '& .MuiTypography-root.gcoin-amount, & .gcoin-amount, & .gcoin-winner': { color: 'var(--joy-palette-primary-300, #c56ffa)' },
    '& .MuiTypography-root.gcoin-inherit': { color: 'inherit' },
    '& .MuiButton-root': { borderRadius: '10px', fontWeight: 700 },
    '& .MuiButton-variantPlain': { color: '#c56ffa' },
    '& .MuiButton-variantOutlined': { color: 'var(--joy-palette-text-primary)', borderColor: 'var(--joy-palette-divider)' },
    '& .MuiButton-root.Mui-disabled': { color: 'var(--joy-palette-text-secondary)', bgcolor: 'var(--joy-palette-background-level2)', opacity: 0.55 },
    '& .MuiChip-root': { color: 'var(--joy-palette-text-secondary)', bgcolor: 'var(--joy-palette-background-level2)' },
    '& .MuiChip-colorSuccess': { color: '#6fe4b7', bgcolor: 'rgba(67,211,158,.12)' },
    '& .MuiChip-colorWarning': { color: '#f5b942', bgcolor: 'rgba(245,185,66,.12)' },
    '& .MuiAlert-root': { bgcolor: 'var(--joy-palette-background-level2)', color: 'var(--joy-palette-text-primary)', border: '1px solid var(--joy-palette-divider)', borderRadius: '12px' },
    '& .MuiAlert-colorWarning': { borderLeft: '3px solid #f5b942' },
    '& .MuiAlert-colorDanger': { borderLeft: '3px solid #ef7777' },
    '& .MuiFormLabel-root, & .MuiCheckbox-label': { color: 'var(--joy-palette-text-primary)' },
};

export const cardSx = { ...surfaceSx, p: { xs: 2, sm: 2.5 }, borderRadius: 'var(--joy-radius-xl)',
    border: '1px solid var(--joy-palette-divider)', bgcolor: 'var(--joy-palette-background-level1)' };
export const dialogSx = { ...cardSx, width: { xs: 'calc(100vw - 24px)', sm: 480 }, maxWidth: 'none',
    boxSizing: 'border-box', minWidth: 0, scrollbarGutter: 'stable',
    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', color: 'var(--joy-palette-text-primary)' };

export function GCoinSegments({ label, options, value, onChange, equalWidth = true }) {
    return <Stack direction="row" role="group" aria-label={label} spacing={0.5} sx={{
        minWidth: 0, flex: 1, p: 0.5, bgcolor: 'var(--joy-palette-background-level2)', borderRadius: 'var(--joy-radius-lg)',
    }}>
        {options.map(([key, text]) => <Button key={key} size="sm" variant="plain" aria-pressed={value === key}
            onClick={() => onChange(key)} sx={{ '&&': {
                flex: equalWidth ? 1 : '1 1 auto', minWidth: 0, minHeight: 36, px: 0.5, whiteSpace: 'nowrap', fontSize: 12,
                color: value === key ? '#fff' : 'var(--joy-palette-text-secondary)',
                background: value === key ? (key === 'month' ? 'linear-gradient(120deg, #6428a4, #45206b)' : 'linear-gradient(120deg, #a226e2, #ba64ed)') : 'transparent',
                boxShadow: value === key ? '0 2px 8px rgba(162,38,226,.2)' : 'none',
                transition: 'background-color .18s, color .18s, box-shadow .18s',
                '&:hover': { transform: 'none', bgcolor: value === key ? undefined : 'rgba(162,38,226,.12)' },
                '&:focus-visible': { outline: '2px solid #dbb2ff', outlineOffset: 2 },
            } }}>{text}</Button>)}
    </Stack>;
}

export function GCoinShell({ title, subtitle, loading, onRefresh, children }) {
    const reduced = useReducedMotion();
    const navigate = useNavigate();
    return <motion.div initial={{ opacity: 0, y: reduced ? 0 : 14 }} animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.28 }}
        style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
        <Header />
        <Box sx={{ ...surfaceSx, maxWidth: 920, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
            <Button variant="plain" color="neutral" startDecorator={<ArrowBackRoundedIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>На главную</Button>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ minWidth: 0 }}><Typography level="h3" sx={{ fontWeight: 800, fontSize: { xs: 23, sm: 28 } }} startDecorator={<GCoinIcon size={26} />}>{title}</Typography>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>{subtitle}</Typography></Box>
                <Button variant="outlined" color="neutral" loading={loading} onClick={onRefresh} aria-label="Обновить" title="Обновить"
                    startDecorator={<RefreshRoundedIcon />} sx={{ borderRadius: 10, flexShrink: 0, whiteSpace: 'nowrap', minWidth: { xs: 38, sm: 'auto' }, px: { xs: 1, sm: 1.5 }, '& .MuiButton-startDecorator': { mr: { xs: 0, sm: 1 } } }}><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Обновить</Box></Button>
            </Stack>
            <Stack spacing={2}>{children}</Stack>
        </Box>
    </motion.div>;
}

export function BalanceCard({ value, caption = 'Баланс G coin' }) {
    const reduced = useReducedMotion();
    return <Box sx={{ ...cardSx, background: 'linear-gradient(125deg, rgba(162,38,226,.16), var(--joy-palette-background-level1) 75%)' }}>
        <Stack direction="row" alignItems="center" spacing={1}><GCoinIcon size={28} />
            <Typography level="body-sm">{caption}</Typography></Stack>
        <motion.div key={value} initial={{ opacity: 0.5, y: reduced ? 0 : 5 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduced ? 0 : 0.2 }}>
            <Typography className="gcoin-amount" sx={{ fontSize: { xs: 32, sm: 40 }, fontWeight: 800, mt: 1, overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}><CoinAmount value={value} iconSize={32} sx={{ fontSize: 'inherit' }} /></Typography>
        </motion.div>
        <Typography level="body-sm" sx={{ mt: 0.5 }}>Начисления и списания сохраняются в истории.</Typography>
    </Box>;
}

export function GCoinLedger({ rows, next, loading, onMore }) {
    return <Box sx={cardSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 1 }}>История операций</Typography>
        {!rows.length && <Typography level="body-sm" sx={{ py: 3 }}>Операций пока нет.</Typography>}
        <Stack divider={<Box sx={{ borderBottom: '1px solid var(--joy-palette-divider)' }} />}>
            {rows.map(row => <Box key={row.id} sx={{ py: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>{ledgerReason(row)}</Typography>
                        <Typography level="body-xs" sx={{ mt: 0.5 }}>{row.account_name} · {gcoinDate(row.created_at)} МСК</Typography>
                        <Typography level="body-xs">{row.actor_name || 'Система'}</Typography>
                        {row.affects_rating && <Typography level="body-xs" sx={{ mt: 0.5 }}>В рейтинге • {row.rating_lane === 'administrator' ? 'Администраторы 🖥️' : 'Фотографы 📸'}</Typography>}
                    </Box>
                    <Chip size="sm" variant="soft" color={String(row.amount).startsWith('-') ? 'warning' : 'success'} sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        <CoinAmount value={row.amount} signed inheritColor sx={{ fontSize: 'inherit' }} />
                    </Chip>
                </Stack>
            </Box>)}
        </Stack>
        {next && <Button variant="plain" color="neutral" fullWidth loading={loading} onClick={onMore} sx={{ mt: 1 }}>Показать ещё</Button>}
    </Box>;
}

export function LoadState({ error, loading, empty }) {
    if (error) return <Alert color="danger" variant="soft" role="alert">{error}</Alert>;
    if (loading && empty) return <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size="sm" aria-label="Загрузка G coin" /></Box>;
    return null;
}

// В старых операциях оставляем исходную причину в аудите, скрываем технический номер в интерфейсе.
export function ledgerReason(row) {
    if (row.display_reason) return row.display_reason;
    if (['revenue', 'revenue_reversal'].includes(row.kind) || /^(?:Выручка|Корректировка выручки)\s*[·•]/i.test(row.reason || '')) {
        const lane = /администратор/i.test(row.reason || '') ? 'Администратор' : 'Фотограф';
        return `${row.kind === 'revenue_reversal' || /^Корректировка/i.test(row.reason || '') ? 'Корректировка продажи' : 'Продажа'} • ${lane}`;
    }
    return row.reason;
}

export function CoinAmount({ value, sx, iconSize = 18, signed = false, inheritColor = false, ...props }) {
    const text = coinText(value);
    return <Typography component="span" className={inheritColor ? 'gcoin-inherit' : 'gcoin-amount'}
        aria-label={`${signed && Number(value) > 0 ? '+' : ''}${text} G coin`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, verticalAlign: 'middle',
            color: inheritColor ? 'inherit' : 'var(--joy-palette-primary-300, #c56ffa)', fontWeight: 800,
            fontVariantNumeric: 'tabular-nums', lineHeight: 'inherit', ...sx }} {...props}>
        <span>{signed && Number(value) > 0 ? '+' : ''}{text}</span><GCoinIcon size={iconSize} />
    </Typography>;
}

// Уведомления — обычный текст: безопасно заменяем только суммы, без HTML из API.
export function CoinText({ text = '' }) {
    return String(text).split(/([+-]?\d+(?:[.,]\d+)?\s*G(?:\s*coin)?\b)/g).map((part, index) => {
        const match = part.match(/^([+-]?\d+(?:[.,]\d+)?)\s*G(?:\s*coin)?$/);
        return match ? <CoinAmount key={index} value={match[1].replace(',', '.').replace(/^\+/, '')} signed={match[1].startsWith('+')} sx={{ fontSize: 'inherit' }} /> : part;
    });
}

export function UpdatingLine({ loading }) {
    return <Box sx={{ height: 3, my: 1, overflow: 'hidden', borderRadius: 2 }}>
        <LinearProgress aria-hidden={!loading} aria-label="Обновление данных" sx={{ opacity: loading ? 1 : 0, transition: 'opacity .2s', pointerEvents: 'none', '--LinearProgress-thickness': '2px' }} />
    </Box>;
}
